import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { RouteErrorBoundary } from '@/components/errors/route-error-boundary';
import { paths } from '@/config/paths';

import { usePageTracking } from './hooks/use-page-tracking';
import {
  OwnerOnlyRoute,
  SettingsIndexRedirect,
} from './routes/app/settings/owner-only';

const convert = (queryClient: QueryClient) => (m: any) => {
  const { clientLoader, clientAction, default: Component, ...rest } = m;
  return {
    ...rest,
    loader: clientLoader?.(queryClient),
    action: clientAction?.(queryClient),
    Component,
  };
};

export const createAppRouter = (queryClient: QueryClient) =>
  createBrowserRouter([
    {
      path: paths.subscribe.path,
      lazy: () => import('./routes/subscribe').then(convert(queryClient)),
    },
    {
      path: paths.auth.login.path,
      lazy: () => import('./routes/auth/login').then(convert(queryClient)),
    },
    {
      path: paths.auth.register.path,
      lazy: () => import('./routes/auth/register').then(convert(queryClient)),
    },
    {
      path: paths.auth.resetPassword.path,
      lazy: () =>
        import('./routes/auth/reset-password').then(convert(queryClient)),
    },
    {
      path: paths.auth.verifyEmail.path,
      lazy: () =>
        import('./routes/auth/verify-email').then(convert(queryClient)),
    },
    {
      path: paths.auth.newPassword.path,
      lazy: () =>
        import('./routes/auth/new-password').then(convert(queryClient)),
    },
    {
      path: paths.auth.revertMfa.path,
      lazy: () => import('./routes/auth/revert-mfa').then(convert(queryClient)),
    },
    {
      path: paths.auth.manageAccount.path,
      lazy: () =>
        import('./routes/auth/manage-account').then(convert(queryClient)),
    },
    {
      path: paths.app.root.path,
      lazy: () => import('./routes/app/root').then(convert(queryClient)),
      children: [
        {
          ErrorBoundary: RouteErrorBoundary,
          children: [
            {
              path: paths.app.dashboard.path,
              lazy: () =>
                import('./routes/app/dashboard').then(convert(queryClient)),
            },
            {
              path: paths.app.knowledge.path,
              lazy: () =>
                import('./routes/app/knowledge').then(convert(queryClient)),
            },
            {
              path: paths.app.avatars.path,
              lazy: () =>
                import('./routes/app/avatars/index').then(convert(queryClient)),
            },
            {
              path: paths.app.avatars.create.path,
              lazy: () =>
                import('./routes/app/avatars/create').then(
                  convert(queryClient),
                ),
            },
            {
              path: paths.app.agents.path,
              lazy: () =>
                import('./routes/app/agents').then(convert(queryClient)),
            },
            {
              path: paths.app.agents.create.path,
              lazy: () =>
                import('./routes/app/agents/create').then(convert(queryClient)),
            },
            {
              path: paths.app.agent.path,
              lazy: () =>
                import('./routes/app/agents/detail').then(convert(queryClient)),
            },
            {
              path: paths.app.videos.path,
              lazy: () =>
                import('./routes/app/videos/index').then(convert(queryClient)),
            },
            {
              path: paths.app.videos.create.path,
              lazy: () =>
                import('./routes/app/videos/create').then(convert(queryClient)),
            },
            {
              path: paths.app.settings.path,
              lazy: () =>
                import('./routes/app/settings/root').then(convert(queryClient)),
              children: [
                {
                  index: true,
                  element: <SettingsIndexRedirect />,
                },
                {
                  path: paths.app.settings.branding.path,
                  lazy: () =>
                    import('./routes/app/settings/branding').then(
                      convert(queryClient),
                    ),
                },
                {
                  path: paths.app.settings.members.path,
                  lazy: () =>
                    import('./routes/app/settings/members').then(
                      convert(queryClient),
                    ),
                },
                {
                  element: <OwnerOnlyRoute />,
                  children: [
                    {
                      path: paths.app.settings.subscription.path,
                      lazy: () =>
                        import('./routes/app/settings/subscription').then(
                          convert(queryClient),
                        ),
                    },
                    {
                      path: paths.app.settings.invoices.path,
                      lazy: () =>
                        import('./routes/app/settings/invoices').then(
                          convert(queryClient),
                        ),
                    },
                  ],
                },
                {
                  path: paths.app.settings.apiKeys.path,
                  lazy: () =>
                    import('./routes/app/settings/api-keys').then(
                      convert(queryClient),
                    ),
                },
                {
                  path: paths.app.settings.webhooks.path,
                  lazy: () =>
                    import('./routes/app/settings/webhooks').then(
                      convert(queryClient),
                    ),
                },
              ],
            },
            {
              path: paths.app.conversations.path,
              lazy: () =>
                import('./routes/app/conversations').then(convert(queryClient)),
            },
            {
              path: paths.app.conversation.path,
              lazy: () =>
                import('./routes/app/conversation').then(convert(queryClient)),
            },
            {
              path: paths.app.analytics.path,
              lazy: () =>
                import('./routes/app/analytics').then(convert(queryClient)),
            },
            {
              path: paths.app.invite.path,
              lazy: () =>
                import('./routes/app/invite').then(convert(queryClient)),
            },
          ],
        },
      ],
    },
    {
      path: '*',
      lazy: () => import('./routes/not-found').then(convert(queryClient)),
    },
  ]);

export const AppRouter = () => {
  const queryClient = useQueryClient();

  const router = useMemo(() => createAppRouter(queryClient), [queryClient]);

  usePageTracking(router);

  return <RouterProvider router={router} />;
};
